import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CartContext = createContext(null);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const { user, token } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const getHeaders = useCallback(() => {
    const headers = {};
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    return headers;
  }, [token]);

  const fetchCart = useCallback(async () => {
    if (!user) {
      setItems([]);
      setTotal(0);
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/cart`, {
        headers: getHeaders(),
        withCredentials: true
      });
      setItems(response.data.items || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching cart:', error);
    }
    setLoading(false);
  }, [user, getHeaders]);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  const addToCart = async (bookId, quantity = 1) => {
    if (!user) {
      throw new Error('Please login to add items to cart');
    }
    
    await axios.post(`${API}/cart`, 
      { book_id: bookId, quantity },
      { headers: getHeaders(), withCredentials: true }
    );
    await fetchCart();
  };

  const updateQuantity = async (cartItemId, quantity) => {
    await axios.put(`${API}/cart/${cartItemId}?quantity=${quantity}`, {},
      { headers: getHeaders(), withCredentials: true }
    );
    await fetchCart();
  };

  const removeFromCart = async (cartItemId) => {
    await axios.delete(`${API}/cart/${cartItemId}`,
      { headers: getHeaders(), withCredentials: true }
    );
    await fetchCart();
  };

  const clearCart = async () => {
    await axios.delete(`${API}/cart`,
      { headers: getHeaders(), withCredentials: true }
    );
    setItems([]);
    setTotal(0);
  };

  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);

  const value = {
    items,
    total,
    loading,
    itemCount,
    addToCart,
    updateQuantity,
    removeFromCart,
    clearCart,
    fetchCart
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};
